import { useCallback, useEffect, useMemo, useState } from 'react'

import { ethers } from 'ethers'
import Web3Modal from 'web3modal'
import { Web3Provider } from '@ethersproject/providers'
import { useEthers } from 'hooks/useEthers'

// Constants
import { WALLET_INITIAL_STATE } from './useWallet.constants'
import {
  PRODUCTION_NETWORK_PARAMS,
  TESTNET_NETWORK_PARAMS,
  ETHEREUM_NETWORK_PARAMS,
} from './useWallet.constants'
import { getIsTestNetwork } from 'utils/getIsTestNetwork'
import {
  ChainParams,
  WalletInternalState,
  WalletReturnType,
} from './useWallet.types'
import WalletConnectProvider from '@walletconnect/web3-provider'
import CoinbaseWalletSDK from '@coinbase/wallet-sdk'

export const useWallet = (): WalletReturnType => {
  const [walletState, setWalletState] = useState(WALLET_INITIAL_STATE)
  const [provider, setProvider] = useState<Web3Provider | null>(null)

  const isTestNetwork = useMemo(() => getIsTestNetwork(), [])
  const ethBridgeNetwork = ETHEREUM_NETWORK_PARAMS;

  const targetNetwork = useMemo(
    () => (isTestNetwork ? TESTNET_NETWORK_PARAMS : PRODUCTION_NETWORK_PARAMS),
    [isTestNetwork]
  )

  const setState = useCallback(
    <T>(key: keyof WalletInternalState, newValue: T) => {
      setWalletState((current) => ({ ...current, [key]: newValue }))
    },
    []
  )

  const isCorrectChain = useCallback(
      (chainId: string) => chainId == targetNetwork.chainParams.chainId,
      [targetNetwork]
  )

  const isAcceptedChain = useCallback(
    (chainId: string) => chainId == ethBridgeNetwork.chainParams.chainId,
      [ethBridgeNetwork]
  )

    const changeNetwork = async (ethereum: any, chainParams: ChainParams) => {
        try {
            await ethereum.request({
                method: 'wallet_switchEthereumChain',
                params: [{ chainId: chainParams.chainId }],
            })
        } catch (switchError: any) {

            // This error code indicates that the chain has not been added to MetaMask.
            if (switchError.code === 4902) {
                try {
                    await ethereum.request({
                        method: 'wallet_addEthereumChain',
                        params: [chainParams],
                    })
                } catch (addError) {
                    console.error(addError)
                }
            } else {
                console.error(switchError)

            }
        }
    }

  // TODO: Error Handling.
  const swapNetwork = useCallback(
    async (ethereum: any, chainParams: ChainParams, throwCancelled: boolean) => {
      try {
        await ethereum.request({
          method: 'wallet_switchEthereumChain',
          params: [{ chainId: chainParams.chainId }],
        })
      } catch (switchError: any) {
        // This error code indicates that the chain has not been added to MetaMask.
        if (switchError.code === 4902) {
          try {
            await ethereum.request({
              method: 'wallet_addEthereumChain',
              params: [chainParams],
            })
          } catch (addError) {
            console.error(addError)
          }
        } else {
          console.error(switchError)
          if (throwCancelled) throw new Error(switchError)
        }
      }
    },
    []
  )

    const swapToTarget = useCallback((throwCancelled: boolean) => {
      return swapNetwork(provider, targetNetwork.chainParams, throwCancelled)
  }, [swapNetwork, provider, targetNetwork])

  /**
   * Handlers isChainAccepted
   */
  const handleConnect = useCallback(
    (ethereum: any, chainId: string) => {
      try {
      setState(
        'isChainConnected',
        ethereum.isConnected() && isCorrectChain(chainId)
      )
      setState(
        'isChainAccepted',
        ethereum.isConnected() && isAcceptedChain(chainId)
      )
      } catch(err) {
        console.log(err);
      }
    },
    [setState, isCorrectChain, isAcceptedChain]
  )

  const handleDisconnect = useCallback(() => {
    try {
      setState('isChainConnected', false)
      setState('isChainAccepted', false)
    } catch (err) {
      console.log(err);
    }
  }, [setState])

  const handleChainChanged = useCallback(
    (chainId) => {
      setState('chainId', chainId)
      setState('isChainConnected', isCorrectChain(chainId))
      setState('isChainAccepted', isAcceptedChain(chainId))

      return chainId
    },
    [isCorrectChain, isAcceptedChain, setState]
  )

  const handleAccountsChanged = useCallback(
    (accounts: string[]) => {
      try {
      if (accounts.length === 0) {
        // Wallet is locked or the user has not connected any accounts
        setState('isUserConnected', false)
        setState('isWalletLoading', false)
        console.log('Please connect a Wallet.')
      } else {
        const newAddress = ethers.utils.getAddress(accounts[0])
        if (newAddress !== walletState.currentAccount) {
          setState('currentAccount', newAddress)
          setState('isUserConnected', true)
        }
      }
      } catch(err) {
        console.log(err);
      }
    },
    [setState, walletState.currentAccount]
  )

  const getAccounts = useCallback(
    (ethereum: any) => {
      return ethereum
        .request({ method: 'eth_accounts' })
        .then(handleAccountsChanged)
        .catch((err: any) => {
          console.error(err)
        })
    },
    [handleAccountsChanged]
  )

  const getChainId = useCallback(
    async (ethereum: any) => {
      return ethereum
        .request({ method: 'eth_chainId' })
        .then(handleChainChanged)
        .catch((err: any) => {
          console.error(err)
        })
    },
    [handleChainChanged]
  )

  const web3Modal = useMemo(
    () =>
      new Web3Modal({
        network: targetNetwork.network,
        cacheProvider: true,
        providerOptions: {
          walletconnect: {
            package: WalletConnectProvider,
            options: {
              rpc: {
                [targetNetwork.rpcIndex]: targetNetwork.chainParams.rpcUrls[0],
              },
            },
          },
          coinbasewallet: {
            package: CoinbaseWalletSDK,
            options: {
              appName: 'SafePokt',
              rpc: targetNetwork.chainParams.rpcUrls[0],
              chainId: targetNetwork.chainParams.chainId,
              darkMode: true,
            },
          },
        },
      }),
    [targetNetwork]
  )

  const connect = useCallback(async () => {
    try {
      const instance = await web3Modal.connect()
      setProvider(instance)
    } catch (err:any) {
      console.error(err.message)
    }
  }, [web3Modal])

  useEffect(() => {
    const startAppWithProvider = async () => {
      try {
      if (!provider) {
        setState('isWalletLoading', false)
        setState('isUserConnected', false)

        return
      }

      // Subscribe to accounts change
      await getAccounts(provider)
      provider.on('accountsChanged', handleAccountsChanged)

      // Subscribe to chainId change
      const chainId = await getChainId(provider)
      provider.on('chainChanged', handleChainChanged)

      // Subscribe to provider connection
      handleConnect(provider, chainId)
      provider.on('connect', () => handleConnect(provider, chainId))

      // Subscribe to provider disconnection
      provider.on('disconnect', handleDisconnect)

      setState('isWalletLoading', false)
      } catch(err) {
        console.log("E"+err);
      }
    }

    startAppWithProvider()
  }, [
    provider,
    setState,
    getChainId,
    getAccounts,
    handleAccountsChanged,
    handleChainChanged,
    handleConnect,
    handleDisconnect,
  ])

  useEffect(() => {
    try {
    if (web3Modal.cachedProvider) {
      connect()
    }
    } catch(err) {
      console.log("E"+err);
    }
  }, [web3Modal, connect])

  const ethersProvider = useEthers(
    provider,
    walletState.isChainConnected,
    targetNetwork.chainParams.rpcUrls[0],
    targetNetwork.wsurl
  )

  const bridgeEthersProvider = useEthers(
    provider,
    walletState.isChainAccepted,
    ethBridgeNetwork.chainParams.rpcUrls[0],
    ethBridgeNetwork.wsurl
  )

  return {
    ...walletState,
    connect,
    swapToTarget,
    changeNetwork,
    ethersProvider,
    bridgeEthersProvider,
    provider,
    targetNetwork,
    ethBridgeNetwork,
    isTestNetwork,
  }
}